import Image from "next/image";
import Link from "next/link";
import React, { useState } from "react";
import { useRouter } from "next/router";
import { En, Fa } from "@/components/ui/multilang";
import {
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getSortedRowModel,
  FilterFn,
  createColumnHelper,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import Space from "@/components/ui/Space";
import persianJs from "persianjs";
import Search from "../classes-page-components/Search";
import { toReadableDate } from "@/utils/formatter";

type Workshop = {
  id: string;
  slug: string;
  title: string;
  titleFa: string;
  from: string;
  to: string;
  subtitle: string;
  subtitleFa: string;
  coverPhoto: string;
  isFree: boolean;
  teacherFullname: string;
  teacherFullnameFa: string;
  language: string;
  date: string;
  ageGroup: string;
  ageGroupFa: string;
  price: number;
};

export default function WorkshopsGrid({ data }: { data: any[] }) {
  const columnHelper = createColumnHelper<Workshop>();
  const columns = [
    columnHelper.accessor("id", {}),
    columnHelper.accessor("slug", {}),
    columnHelper.accessor("title", {}),
    columnHelper.accessor("titleFa", {}),
    columnHelper.accessor("from", {}),
    columnHelper.accessor("to", {}),
    columnHelper.accessor("subtitle", {}),
    columnHelper.accessor("subtitleFa", {}),
    columnHelper.accessor("coverPhoto", {}),
    columnHelper.accessor("isFree", {}),
    columnHelper.accessor("teacherFullname", {}),
    columnHelper.accessor("teacherFullnameFa", {}),
    columnHelper.accessor("language", {}),
    columnHelper.accessor("date", {}),
    columnHelper.accessor("ageGroup", {}),
    columnHelper.accessor("ageGroupFa", {}),
    columnHelper.accessor("price", {}),
  ];

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);

    addMeta({
      itemRank,
    });

    return itemRank.passed;
  };

  const table = useReactTable({
    data,
    columns,
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugTable: true,
    debugHeaders: true,
    debugColumns: false,
  });

  function getPaginatedData(bigArray: any[], size = 24): any[] {
    const arrayOfArrays = [];
    for (let i = 0; i < bigArray.length; i += size) {
      arrayOfArrays.push(bigArray.slice(i, i + size));
    }
    return arrayOfArrays;
  }

  const { locale } = useRouter();

  return (
    <div className="  relative flex flex-col w-full ">
      <div className="sticky top-0 z-10 b-b-1 b-sand5 ">
        <div className="relative pb-4 bg-[#FCF9F7] py-4 ">
          <div className="flex flex-wrap ac gap-4 justify-between bg-[#FCF9F7] ">
            <Search
              value={globalFilter}
              onChange={(value) => setGlobalFilter(String(value))}
              placeholder={locale === "en" ? "Search tutors or subjects" : "جستجو بین معلم‌ها و کلاس‌ها..."}
            />
          </div>

          <Shadow />
        </div>
      </div>
      <div>
        <Space size="h-8" />
        <ul className="flex flex-col gap-6 ">
          {getPaginatedData(table.getRowModel().rows).map((groupData) =>
            groupData.map((row) => {
              return (
                <WorkshopCardForTableHook
                  key={row.getValue("id")}
                  id={row.getValue("id")}
                  slug={row.getValue("slug")}
                  title={row.getValue("title")}
                  titleFa={row.getValue("titleFa")}
                  coverPhoto={row.getValue("coverPhoto")}
                  isFree={row.getValue("isFree")}
                  date={row.getValue("date")}
                  teacherFullname={row.getValue("teacherFullname")}
                  teacherFullnameFa={row.getValue("teacherFullnameFa")}
                  price={row.getValue("price")}
                />
              );
            })
          )}
        </ul>
      </div>
      <div className="mt-auto"></div>
    </div>
  );
}

function Shadow() {
  return (
    <div className="h-10 absolute top-0 w-full translate-y-90% -z-1 px-10">
      <div className="bg-[#301f12] opacity-10 blur-20 rd-b-50% w-full h-full mix-blend-multiply"></div>
    </div>
  );
}

function WorkshopCardForTableHook({
  id,
  slug,
  title,
  titleFa,
  coverPhoto,
  isFree,
  date,
  teacherFullname,
  teacherFullnameFa,
  price,
}) {
  const { locale } = useRouter();
  return (
    <li key={id} className="hover:bg-sand4 p-4 rounded-xl">
      <Link href={`/workshops/${slug}`} className="block">
        <div className="flex flex-col sm:flex-row sm:justify-start gap-4">
          <div className="grid relative isolate w-64 h-36 sm:w-80 sm:h-45">
            <Image
              src={coverPhoto || ""}
              fill
              alt={locale === "en" ? teacherFullname : teacherFullnameFa}
              className="rd-xl object-cover rd-4 sm:rd-6 bg-sand4 shd-tinted-2 b-2 b-white"
            />
          </div>
          <div>
            <div className="flex justify-between items-center">
              <Fa>
                <h3 className="c-sand11 text-xl fw-900" title={titleFa}>
                  {titleFa}
                </h3>
              </Fa>
              <En>
                <h3 className="c-sand11 fs-xl sm:fs-2xl fw-600" title={title}>
                  {title}
                </h3>
              </En>
            </div>
            <div className="">
              <p className="c-sand11 ">
                <Fa>{teacherFullnameFa}</Fa>
                <En>{teacherFullname}</En>
              </p>
              <div className="flex justify-between items-baseline  ">
                <p className="c-sand11 flex items-baseline">
                  {isFree ? (
                    <>
                      <En>Free</En>
                      <Fa>رایگان</Fa>
                    </>
                  ) : (
                    <>
                      <En>${price} CAD</En>
                      <Fa>
                        {persianJs(price).englishNumber().toString()}
                        {` `}
                        دلار کانادا
                      </Fa>
                    </>
                  )}
                </p>
              </div>
              <p className="c-sand11 ">{toReadableDate(date)}</p>
            </div>
          </div>
        </div>
      </Link>
    </li>
  );
}
